import axios from 'axios';
import Cookies from 'js-cookie';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_URL = `${BASE_URL}/api/auth`; // Full URL to the auth endpoint

// Create a separate axios instance for token refresh to avoid interceptor loops
const refreshAxios = axios.create({
  baseURL: BASE_URL,
});

const authService = {
  login: async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/login/`, {
        email,
        password
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { non_field_errors: ['An error occurred'] };
    }
  },

  signup: async (email, fullName, password) => {
    try {
      const response = await axios.post(`${API_URL}/signup/`, {
        email,
        full_name: fullName,
        password
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { non_field_errors: ['An error occurred'] };
    }
  },

  refreshToken: async () => {
    try {
      const refreshToken = Cookies.get('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await refreshAxios.post(`${API_URL}/token/refresh/`, {
        refresh: refreshToken
      });

      const { access } = response.data;
      Cookies.set('accessToken', access, { 
        secure: true, 
        sameSite: 'strict',
        expires: 1/24 // 1 hour
      });

      return access;
    } catch (error) {
      throw error.response?.data || { detail: 'Failed to refresh token' };
    }
  },

  setTokens: (accessToken, refreshToken) => {
    // Set tokens in HttpOnly cookies
    Cookies.set('accessToken', accessToken, { 
      secure: true, 
      sameSite: 'strict',
      expires: 1/24 // 1 hour
    });
    
    Cookies.set('refreshToken', refreshToken, { 
      secure: true, 
      sameSite: 'strict',
      expires: 7 // 7 days
    });
  },

  logout: () => {
    Cookies.remove('accessToken');
    Cookies.remove('refreshToken');
  },

  // OTP Authentication methods
  sendOTP: async (email) => {
    try {
      const response = await axios.post(`${API_URL}/verify-email/`, {
        email
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'An error occurred while sending OTP' };
    }
  },

  verifyOTP: async (email, otp) => {
    try {
      const response = await axios.post(`${API_URL}/verify-otp/`, {
        email,
        otp
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'An error occurred while verifying OTP' };
    }
  },

  // Google OAuth Authentication
  googleOAuth: async (authorizationCode) => {
    try {
      const response = await axios.post(`${API_URL}/google-oauth/`, {
        code: authorizationCode,
        redirect_uri: window.location.origin
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Google authentication failed' };
    }
  },

  // Contact Form API
  getContactConfig: async () => {
    try {
      const response = await axios.get(`${API_URL}/contact/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to fetch contact form configuration' };
    }
  },

  submitContactForm: async (formData) => {
    try {
      // Get JWT token if user is authenticated
      const accessToken = Cookies.get('accessToken');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await axios.post(`${API_URL}/contact/`, formData, {
        headers
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to submit contact form' };
    }
  },

  // Get current user from JWT token
  getUser: () => {
    const token = Cookies.get('accessToken');
    if (!token) return null;
    
    try {
      // Decode JWT token (format: header.payload.signature)
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window.atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join('')
      );
      
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error decoding token:', e);
      return null;
    }
  }
};

export default authService;
